import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SidebarService } from '@ibe/services/ui';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import * as fromStore from '../../../../../store';

import { PassengersSegmentedCount } from '@ibe.models/passenger.model.indra.lib';
import { INDRAFeature } from '@ibe.models/ancillary.indra.model.lib';
import { INDRAAncillariesService } from '@ibe.services/ancillaries.indra.service';
import { INDRABookingSearchService } from '@ibe.services/booking-search.indra.service';
import { INDRABookingService } from '@ibe.services/booking.indra.service';

import { COUNTRY_CODE_USA,
		BRAND_CODE_EXECUTIVE,
		BRAND_CODE_TOP_EXECUTIVE,
		ANCILLARY_PRODUCT_GROUP_CODE_HOLD_BAGGAGE,
		EXTRAS_ANCILLARY_BAG,
		ANCILLARY_PRODUCT_BAGGAGE,
		PAX_TYPE_INFANT_CODE, 
		SESSION_VAR_KEY_LANGUAGE,
		DEFAULT_LANGUAGE,
		SESSION_VAR_KEY_MARKET,
		DEFAULT_MARKET,
		SPECIAL_CASE_MARKET_LANGUAGE_MAP} from '@ibe.config';
import { INDRAIbeAdminConfigService } from '@ibe.services/ibeadmin-config.service';
import { INDRAIbeAdminService } from '@ibe.services/ibeadmin.indra.service';

@Component({
	selector: 'app-flight-details-luggage',
	templateUrl: './details-luggage.indra.component.html',
	styleUrls: ['./details-luggage.component.scss']
})
export class INDRAFlightDetailsLuggageComponent implements OnInit, OnDestroy {
	flight;
	brand: string;
	feature: INDRAFeature;
	flightIdentifier;

	isAlive = true;

	passengers$: Observable<PassengersSegmentedCount> = this.store.select(fromStore.getMiniIBEPassengers);
	passengerList;

	isUSA: boolean = false;
	maxBagWeightAllowance;
	reviewFlights: boolean = false;
	timeToThinkFlights: boolean = false;
	showAddButton: boolean = false;
	hasInfantPassenger: boolean = false;

	constructor(
		protected sidebarService: SidebarService,
		protected route: ActivatedRoute,
		protected router: Router,
		private store: Store<fromStore.State>,
		private ancillariesService: INDRAAncillariesService,
		private bookingSearchService: INDRABookingSearchService,
		private bookingService: INDRABookingService,
		private ibeAdminConfigService: INDRAIbeAdminConfigService,
		private ibeAdminService: INDRAIbeAdminService
	) {
	}

	ngOnInit() {
		if (this.router.url.includes('/review-your-trip')) this.reviewFlights = true;
		if (this.router.url.includes('/time-to-think')) this.timeToThinkFlights = true;
		this.brand = this.route.snapshot.paramMap.get('brand');

		this.passengers$
			.pipe(takeWhile(() => this.isAlive))
				.subscribe(value => {
				if (value) {
					// generate generic passenger list according to type and count
					this.passengerList = this.ancillariesService.INDRA_generateGenericPassengerList(value);
				}
			});

		// Note: There are three flows where this sidebar will be shown - flight search result page, review your trip page, and in time to think page
		if (this.reviewFlights || this.timeToThinkFlights) {
			this.flightIdentifier = this.route.snapshot.paramMap.get('flight');
			const flightObj = this.bookingService.INDRA_getBookedFlight(this.flightIdentifier);
			this.flight = this.bookingService.INDRA_getFlightRoute(flightObj);
			this.feature = this.bookingService.INDRA_getIncludedFeature(
				ANCILLARY_PRODUCT_GROUP_CODE_HOLD_BAGGAGE, this.flight.idFlight.toString(), '');
			this.INDRA_showAddButton(flightObj, this.flightIdentifier);
		} else {
			// get flight and feature object from the data obj passed into the sidebarservice
			const parameters = this.sidebarService.getSidebarParameters();
			if (parameters && parameters.length) {
				this.flight = parameters[0].flightInfo;
				this.feature = parameters[0].feature;
			}
		}

		// set sidebar specific properties
		this.INDRA_setSpecialRules();
		this.INDRA_setBaggageAllowance();
		this.INDRA_setHasInfantPassenger();
	}

	INDRA_gotoURL(key) {
		const languageCode = sessionStorage.getItem(SESSION_VAR_KEY_LANGUAGE) ?
								sessionStorage.getItem(SESSION_VAR_KEY_LANGUAGE).toLowerCase() : DEFAULT_LANGUAGE;
		const languageMap = SPECIAL_CASE_MARKET_LANGUAGE_MAP[languageCode] ? SPECIAL_CASE_MARKET_LANGUAGE_MAP[languageCode] : languageCode;
		const marketCode = sessionStorage.getItem(SESSION_VAR_KEY_LANGUAGE) ?
								sessionStorage.getItem(SESSION_VAR_KEY_MARKET).toLowerCase() : DEFAULT_MARKET;
		const urlIbeAdmin = this.ibeAdminConfigService.getConfigurationValue(key);
		const urlNavigate = urlIbeAdmin.replace('${lang}', `${languageMap}`).replace('${market}', `${marketCode}`);
		return window.open(urlNavigate);
	}

	INDRA_setHasInfantPassenger() {
		const infantPassengers = this.passengerList.filter(passenger => passenger.type === PAX_TYPE_INFANT_CODE);
		if (infantPassengers.length) this.hasInfantPassenger = true;
	}

	INDRA_setSpecialRules() {
		const countries = (this.reviewFlights) ? this.bookingService.INDRA_getFlightSegmentsCountryList(this.flight.listSegment)
			: this.bookingSearchService.INDRA_getFlightSegmentsCountryList(this.flight.listSegment) ;
		this.isUSA = countries.includes(COUNTRY_CODE_USA);
	}

	// set max bag weight allowance per piece
	INDRA_setBaggageAllowance() {
		if (this.brand === BRAND_CODE_EXECUTIVE || this.brand === BRAND_CODE_TOP_EXECUTIVE) {
			this.maxBagWeightAllowance = this.INDRA_getTranslation('whatsIncludedSidebars.detailsLuggage.texts.maxWeightSpecsBusiness');
		} else {
			this.maxBagWeightAllowance = this.INDRA_getTranslation('whatsIncludedSidebars.detailsLuggage.texts.maxWeightSpecs');
		}
	}

	INDRA_showAddButton(flight, flightIdentifier) {
		const ancillaryAvailable =
			this.bookingService.INDRA_isAncillaryCatalogAvailableForBooking(ANCILLARY_PRODUCT_BAGGAGE, flight);
		const ancillaryBookable = this.bookingService.INDRA_isAncillaryAvailable(EXTRAS_ANCILLARY_BAG, flightIdentifier);
		const isCodeshare = this.bookingService.INDRA_checkIfCodeShare(flight.listSegment);
		if (ancillaryAvailable &&  ancillaryBookable && !isCodeshare) {
			this.showAddButton = true;
		}
	}


	// SIDEBAR INTERACTIONS

	/** Close sidebar and clear secondary route */
	private closeSidebar() {
		this.sidebarService.closeSidebar();
		this.router.navigate([{ outlets: { sidebar: null } }], { replaceUrl: true });
	}

	/** Handles close action intention **/
	INDRA_handleClose() { this.closeSidebar(); }

	openExtraBagsSidebar() {
		this.sidebarService.openSidebar(['checked-bags', { flight: this.flightIdentifier }]);
	}

	ngOnDestroy() {
		this.isAlive = false;
	}

	INDRA_printImage() {
		const IMAGE = 'checked_baggage.jpg';
		return 'url(assets/images/ancillaries/includes/' + IMAGE + ')';
	}

	INDRA_getTranslation(key: string): string {
		return this.ibeAdminService.getTranslationValue(key);
	}
}
